import { AvailableThemes, ThemeProvider, ThemeProviderContext } from "@centricsoftware/design-system";
import { useTheme } from "@mui/material";
import html from "csi/html";
import { dataSetupStore, DataSetupStoreContext } from "csi/state/datasetup";
import { RootStoreContext, useStore } from "csi/state/hooks";
import rootStore from "csi/state/rootStore";
import { useResizeObserver } from "designSystem/hooks";
import { Box, Overlay } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import topic from "dojo/topic";
import _ from "lodash";
import { observer } from "mobx-react-lite";
import React, { Fragment, ReactElement, useContext, useEffect } from "react";
import ReactDom from "react-dom";
import Breadcrumb from "../Breadcrumb";
import RightPane from "../RightPane";
import styles from "./ReactPortals.module.css";

interface SlotProps {
	children: ReactElement;
	containerElement?: HTMLElement;
	type?: "banner" | "breadcrumb" | "feature" | "body" | "inline";
}

const CENTER_STYLES = {
	flexGrow: 1,
	height: "100%",
	overflow: "hidden",
	position: "relative",
};

const HIDE_STYLES = {
	display: "none",
};

const RIGHT_STYLES = {
	width: "100%",
};

const breadcrumbNode = document.getElementById("pageBreadcrumb");
const pageBannerNode = document.getElementById("pageBanner");
const centerNode = html.pageCenterSplit();

export const getSlotContainerName = (type: SlotProps["type"]) => `csi-slot-${type}-container`;

const SlotWithThemeProvider = ({ containerElement, children, type }: SlotProps) => {
	const loc = useLocale();
	const id = getSlotContainerName(type);

	return (
		ReactDom.createPortal(
			<ThemeProvider
				className={styles.themeProviderWrapper}
				getLocalizedText={loc.resource}
				id={id}
				theme={AvailableThemes.centricBlueTheme}
			>
				{children}
			</ThemeProvider>,
			containerElement,
		)
	);
};

const BannerSlot = ({ children }: SlotProps) => SlotWithThemeProvider({ containerElement: pageBannerNode, children, type: "banner" });
const BreadcrumbSlot = ({ children }: SlotProps) => SlotWithThemeProvider({ containerElement: breadcrumbNode, children, type: "breadcrumb" });
const FeatureSlot = ({ children }: SlotProps) => SlotWithThemeProvider({ containerElement: centerNode, children, type: "feature" });
const BodySlot = ({ children }: SlotProps) => SlotWithThemeProvider({ containerElement: document.body, children, type: "body" });
const InlineSlot = ({ children }: SlotProps) => SlotWithThemeProvider({ containerElement: document.body, children, type: "inline" });

/** instruct all top-level views to resize when page content size changes */
const onResizePageContent = _.throttle(() => {
	topic.publish("/csi/window/resize");
}, 250);

const Banner = observer(() => {
	const { routerStore: { bannerComponent, locationNode } } = useStore();
	let BannerBody = null;

	if (locationNode) {
		BannerBody = bannerComponent?.component;
	}

	return (
		<>
			{BannerBody &&
				<BannerBody {...bannerComponent.properties} />
			}
		</>
	);
});

const PageCenter = observer(() => {
	const {
		layoutVisible,
		rightPaneStore: { isFullWidth, focusRight },
		routerStore: { centerFloatComponent, featureComponent },
	} = useStore();

	// if the 3D viewer is open, render the right pane in the 3D viewer dialog, if not, render it on the page
	const render3DRightPane = rootStore.threeDStore?.viewerId;

	const { ref } = useResizeObserver<HTMLDivElement>({ onResize: onResizePageContent });
	const Feature = featureComponent?.component;
	const FloatComponent = centerFloatComponent?.component;

	return (
		<Fragment>
			{Feature && (
				<Box
					id="pageContent"
					ref={ref}
					sx={layoutVisible ? null : HIDE_STYLES}
				>
					<Feature {...featureComponent.properties} />
					{focusRight &&
						<Overlay open />
					}
				</Box>
			)}
			{FloatComponent && (
				<Box
					id="pageCenterFloat"
					sx={isFullWidth ? HIDE_STYLES : CENTER_STYLES}
				>
					<FloatComponent {...centerFloatComponent.properties} />
				</Box>
			)}
			<Box
				id="pageFocused"
				sx={isFullWidth ? RIGHT_STYLES : null}
			>
				{!render3DRightPane &&
					<RightPane />
				}
			</Box>
		</Fragment>
	);
});

export const ReactPortals = observer(() => {
	const { routerStore: { bodyComponent, inlineComponent, showBreadcrumb, renderBreadcrumb }, pageStore, pageContext } = rootStore;
	const Body = bodyComponent?.component;
	const Inline = inlineComponent?.component;

	html.setVisible(breadcrumbNode, showBreadcrumb);
	const theme = useTheme();

	// centric design system theme
	const cdsTheme = useContext(ThemeProviderContext);

	// set up the spacing CSS variable so that they'll be available all across the React component
	useEffect(() => {
		for (let i = 1; i < 10; i++) {
			document.documentElement.style.setProperty(`--spacing-${i}`, theme.spacing(i));
		}
	}, []);

	return (
		<RootStoreContext.Provider value={rootStore}>
			<DataSetupStoreContext.Provider value={dataSetupStore}>
				{renderBreadcrumb ? (
					<BreadcrumbSlot>
						<Breadcrumb />
					</BreadcrumbSlot>
				)
					: null
				}
				{pageContext && (
					<pageContext.Provider value={pageStore}>
						<BannerSlot>
							<Banner />
						</BannerSlot>
					</pageContext.Provider>
				)}
				<FeatureSlot>
					<PageCenter />
				</FeatureSlot>
				{Body ? (
					<BodySlot>
						<Body {...bodyComponent.properties} />
					</BodySlot>
				)
					:
					null
				}
				{Inline ? (
					<InlineSlot>
						<Inline {...inlineComponent.properties} />
					</InlineSlot>
				)
					:
					null
				}
			</DataSetupStoreContext.Provider>
		</RootStoreContext.Provider>
	);
});

export default ReactPortals;